up.history = (function () {
    const u = up.util;
    const e = up.element;
    const config = new up.Config(() => ({
        enabled: true,
        updateMetaTags: true,
        restoreTargets: ['body'],
        metaTagSelectors: [
            'meta',
            'link[rel=alternate]',
            'link[rel=canonical]',
            'link[rel=icon]',
            '[up-meta]',
            'script[type="application/ld+json"]',
        ],
        noMetaTagSelectors: [
            'meta[http-equiv]',
            '[up-meta=false]',
            'meta[name=csp-nonce]',
        ],
    }));
    let previousLocation;
    let nextPreviousLocation;
    let nextTrackOptions;
    let adoptedBases = new up.FIFOCache({ capacity: 100, normalizeKey: getBase });
    function reset() {
        previousLocation = undefined;
        nextPreviousLocation = undefined;
        nextTrackOptions = undefined;
        adoptedBases.clear();
        trackCurrentLocation({ reason: null, alreadyHandled: true });
        adoptBase();
    }
    function refineOption(existingValue, newValue) {
        if ((existingValue === false) || u.isString(existingValue)) {
            return existingValue;
        }
        else {
            return newValue;
        }
    }
    function historyOptions(options) {
        return u.pick(options, [
            'location',
            'title',
            'metaTags',
            'lang'
        ]);
    }
    function currentLocation() {
        return u.normalizeURL(location.href);
    }
    function withTrackOptions(trackOptions, fn) {
        try {
            nextTrackOptions = trackOptions;
            fn();
        }
        finally {
            nextTrackOptions = undefined;
        }
    }
    function trackCurrentLocation(trackOptions) {
        let { reason, alreadyHandled, pauseTracking } = nextTrackOptions || trackOptions;
        if (pauseTracking)
            return;
        let location = currentLocation();
        if (isAdoptedState()) {
            adoptBase(location);
        }
        else if (isAdoptedBase(location)) {
            adoptState();
        }
        if (nextPreviousLocation !== location) {
            previousLocation = nextPreviousLocation;
            nextPreviousLocation = location;
            if (reason === 'detect') {
                reason = (getBase(location) === getBase(previousLocation)) ? 'hash' : 'pop';
            }
            let willHandle = !alreadyHandled && isAdoptedBase(location);
            let locationChangedEvent = up.event.build('up:location:changed', {
                reason,
                location,
                previousLocation,
                alreadyHandled,
                willHandle,
                log: `New location is ${location}`
            });
            up.migrate.prepareLocationChangedEvent?.(locationChangedEvent);
            if (reason) {
                up.emit(locationChangedEvent);
                reactToChange(locationChangedEvent);
            }
        }
    }
    function splitLocation(location) {
        return location?.split(/(?=#)/) || [];
    }
    function getBase(location) {
        return splitLocation(location)[0];
    }
    function isLocation(url, options) {
        return u.matchURLs(url, location.href, { hash: true, ...options });
    }
    function replace(location, trackOptions) {
        placeAdoptedHistoryEntry('replaceState', location, trackOptions);
    }
    function push(location, trackOptions) {
        if (isLocation(location))
            return;
        placeAdoptedHistoryEntry('pushState', location, trackOptions);
    }
    function placeAdoptedHistoryEntry(method, location, trackOptions) {
        adoptBase(location);
        if (config.enabled) {
            withTrackOptions(trackOptions, function () {
                history[method](null, { up: true }, location);
            });
        }
    }
    function isAdoptedBase(location) {
        return !!adoptedBases.get(location);
    }
    function adoptBase(location = currentLocation()) {
        location = u.normalizeURL(location);
        adoptedBases.set(location, true);
    }
    function isAdoptedState() {
        return history.state?.up;
    }
    function adoptState() {
        let { state } = history;
        if (isAdoptedState())
            return;
        if (u.isBlank(state) || u.isObject(state)) {
            withTrackOptions({ pauseTracking: true }, function () {
                history.replaceState({ ...state, up: true }, '');
            });
        }
    }
    function restoreLocation(location) {
        up.error.muteUncriticalRejection(up.render({
            guardEvent: up.event.build('up:location:restore', { location, log: `Restoring location ${location}` }),
            url: location,
            target: config.restoreTargets,
            fail: false,
            history: true,
            location,
            peel: true,
            layer: 'root',
            cache: 'auto',
            revalidate: 'auto',
            saveScroll: false,
            scroll: ['restore', 'auto'],
            saveFocus: false,
            focus: ['restore', 'auto'],
        }));
    }
    function reactToChange(event) {
        if (event.alreadyHandled) {
            return;
        }
        if (!event.willHandle) {
            up.puts('up.history', 'Ignoring history entry owned by foreign script');
            return;
        }
        if (event.reason === 'pop') {
            up.viewport.saveFocus({ location: event.previousLocation });
            up.viewport.saveScroll({ location: event.previousLocation });
            restoreLocation(event.location);
        }
        else if (event.reason === 'hash') {
            up.viewport.revealHash(location.hash, { strong: true });
        }
    }
    function findMetaTags(head = document.head) {
        return head.querySelectorAll(config.selector('metaTagSelectors'));
    }
    function updateMetaTags(newMetaTags) {
        let oldMetaTags = findMetaTags();
        for (let oldMetaTag of oldMetaTags) {
            oldMetaTag.remove();
        }
        for (let newMetaTag of newMetaTags) {
            document.head.append(newMetaTag);
        }
    }
    function getLang(doc = document) {
        let { documentElement } = doc;
        if (documentElement.matches('html')) {
            return doc.documentElement.lang;
        }
    }
    function updateLang(newLang) {
        e.setAttrPresence(e.root, 'lang', newLang, !!newLang);
    }
    function patchHistoryAPI() {
        const originalPushState = history.pushState;
        history.pushState = function (...args) {
            originalPushState.apply(this, args);
            trackCurrentLocation({ reason: 'push', alreadyHandled: true });
        };
        const originalReplaceState = history.replaceState;
        history.replaceState = function (...args) {
            originalReplaceState.apply(this, args);
            trackCurrentLocation({ reason: 'replace', alreadyHandled: true });
        };
    }
    function adoptInitialHistoryEntry() {
        if (up.protocol.initialRequestMethod() === 'GET') {
            adoptState();
            adoptBase();
        }
    }
    up.on('up:framework:boot', function ({ mode }) {
        trackCurrentLocation({ reason: null, alreadyHandled: true });
        patchHistoryAPI();
        adoptInitialHistoryEntry();
        if (mode === 'auto') {
            up.viewport.revealHash(location.hash, { strong: true, focus: true });
        }
    });
    up.on(window, 'hashchange, popstate', () => {
        trackCurrentLocation({ reason: 'detect', alreadyHandled: false });
    });
    function onJumpLinkClicked(event, link) {
        if (event.defaultPrevented)
            return;
        if (up.event.isModified(event))
            return;
        let [currentBase, currentHash] = splitLocation(up.layer.current.location);
        let [linkBase, linkHash] = splitLocation(u.normalizeURL(link));
        let verbatimHREF = link.getAttribute('href');
        let isJumpLink = (currentBase === linkBase) || verbatimHREF.startsWith('#');
        if (!isJumpLink)
            return;
        let behavior = link.getAttribute('up-scroll-behavior') ?? 'auto';
        let layer = up.layer.get(link);
        let revealFn = up.viewport.revealHashFn(linkHash, { layer, behavior, focus: true });
        if (revealFn) {
            up.event.halt(event);
            up.log.putsEvent(event);
            if (linkHash !== currentHash && layer.showsLiveHistory()) {
                let newHREF = currentBase + linkHash;
                push(newHREF, { reason: 'hash', alreadyHandled: true });
            }
            revealFn();
        }
        else {
        }
    }
    up.on('up:click', 'a[href*="#"]', onJumpLinkClicked);
    up.macro('[up-back]', function (link) {
        if (previousLocation) {
            e.setMissingAttrs(link, {
                'up-href': previousLocation,
                'up-scroll': 'restore'
            });
            link.removeAttribute('up-back');
            up.link.makeFollowable(link);
        }
    });
    up.on('up:framework:reset', reset);
    return {
        config,
        push,
        replace,
        get location() { return currentLocation(); },
        get previousLocation() { return previousLocation; },
        isLocation,
        findMetaTags,
        updateMetaTags,
        getLang,
        updateLang,
        refineOption,
        options: historyOptions,
    };
})();
